import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { TextWithLoadingState, TextWithLoadingStateProps } from '@/components/text-with-loading-state';
import { TokenIconList } from '@/components/token-icon-list';
import { TooltipIcon } from '@/components/tooltip-icon';
import { TooltipText } from '@/components/tooltip-text';
import { NucleusVaultMetadata } from '@/constants/nucleus-vaults';
import { ESTIMATED_APY_TOOLTIP_TEXT } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { useRoycoPositionDataForVaultKey } from '@/hooks/useRoycoPositionsData';
import { useVaultApy } from '@/hooks/useVaultApy';
import { useVaultTvl } from '@/hooks/useVaultTvl';
import { getIconPathFromTokenSymbol } from '@/utils';
import {
  Box,
  Card,
  Flex,
  FormatNumber,
  Heading,
  HoverCard,
  HStack,
  Icon,
  Image,
  Portal,
  Skeleton,
  Spacer,
  Stack,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react';
import { getVaultByKey } from '@molecularlabs/nucleus-frontend';
import { useAppKitNetwork } from '@reown/appkit/react';
import { useQuery } from '@tanstack/react-query';
import { ReactElement } from 'react';
import { HiMiniArrowRight } from 'react-icons/hi2';
import { PiChartBarFill } from 'react-icons/pi';
import { useNavigate } from 'react-router';
import { TOTAL_BALANCE_TOOLTIP_TEXT } from '../OptimizerVault/constants';

type StrategyCardProps = {
  vaultMetadata: NucleusVaultMetadata;
  yourDeposits: number;
  yourDepositsUSD: number;
  loading: boolean;
  // roycoPosition: {
  //   balance: number;
  //   isForfeited: boolean;
  //   isWithdrawn: boolean;
  //   unlockTimestamp: number;
  //   receivedIncentives: ReceivedIncentives;
  //   inputToken: string;
  //   marketId: string;
  // };
};

export const StrategyCard = ({ yourDeposits, vaultMetadata, yourDepositsUSD, loading }: StrategyCardProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { chainId } = useAppKitNetwork();

  const { name, summary, vaultAddress, vaultKey, logosPath } = vaultMetadata;
  const { wPlumeEarnings, wPlumeEarningsUsd, inputTokenTotals } = useRoycoPositionDataForVaultKey({ vaultKey });

  const { tvlData } = useVaultTvl({ vaultAddress });

  const { apyData, isLoadingApyData } = useVaultApy({
    vaultAddress,
  });

  const { data: depositTokens = [], isLoading: isLoadingDepositTokens } = useQuery({
    queryKey: ['depositTokens', vaultKey],
    queryFn: async () => {
      if (!chainId) return [];
      const vault = await getVaultByKey(vaultKey);
      return Object.values(vault?.deposit.sourceChains[chainId].depositTokens);
    },
    enabled: Boolean(chainId),
  });

  const totalBalance = (inputTokenTotals?.tokenAmountUsd ?? 0) + yourDepositsUSD + wPlumeEarningsUsd;

  const apyBadge = (
    <RoundedBadge bgColor="#10B9810D" color="#00827B" h={12} px={6}>
      {/* TODO: extract #108258 into semantic symbol */}
      <ValueText loading={isLoadingApyData} color="#108258">
        <FormatNumber value={apyData?.netApy ?? 0} style="percent" /> APY
      </ValueText>
    </RoundedBadge>
  );

  return (
    <Card.Root
      borderRadius="3xl"
      overflow="hidden"
      role="group"
      mb={6}
      _hover={{ transform: 'scale(0.985)', transition: 'transform 0.3s' }}
      cursor="pointer"
      onClick={() => navigate(`/optimizer/${vaultAddress}`)}
      className="group"
    >
      <Card.Body>
        <Stack
          flexDirection={['column', 'column', 'row']}
          gap={4}
          justifyContent="space-between"
          alignItems={['flex-start', 'flex-start', 'center']}
        >
          <HStack justifyContent="space-between" w={['full', 'full', 'auto']}>
            <Image src={getIconPathFromTokenSymbol(vaultMetadata.yieldGeneratingTokenSymbol)} boxSize={12} />
            <HStack hideFrom="lg" gap={4}>
              <TokenIconList tokens={depositTokens} loading={isLoadingDepositTokens} />
              {apyBadge}
            </HStack>
          </HStack>
          <Box>
            <Heading fontWeight="700">{name}</Heading>
            <Heading size="lg" color="fg.muted" fontWeight="500">
              {summary}
            </Heading>
          </Box>
          <Spacer flex={1} />
          <HStack hideBelow="lg" gap={4}>
            <Spacer flex={1} />
            <TokenIconList tokens={depositTokens} loading={isLoadingDepositTokens} />
            {apyBadge}
          </HStack>
        </Stack>
      </Card.Body>
      <Card.Body hideFrom="lg">
        <VStack alignItems="flex-start" w="full">
          <HStack justifyContent="space-between" py={3} w="full" borderBottomWidth="thin" borderColor="border.subtle">
            <Text color="text.secondary">Vault Position</Text>
            <ValueText loading={loading}>
              {isAuthenticated ? (
                <>
                  <FormatNumber value={yourDeposits} /> {vaultKey}
                </>
              ) : (
                '--'
              )}
            </ValueText>
            {inputTokenTotals?.tokenAmount ? (
              <ValueText loading={false} fontSize="sm" color="fg.muted" fontFamily="body">
                <>
                  <FormatNumber value={inputTokenTotals?.tokenAmount} /> held in Royco
                </>
              </ValueText>
            ) : null}
          </HStack>
          <HStack justifyContent="space-between" py={3} w="full" borderBottomWidth="thin" borderColor="border.subtle">
            <Text color="text.secondary">Total Balance</Text>
            <ValueText loading={loading}>
              {isAuthenticated ? <FormatNumber value={totalBalance} style="currency" currency="USD" /> : '--'}
            </ValueText>
          </HStack>
          <HStack justifyContent="space-between" py={3} w="full" borderBottomWidth="thin" borderColor="border.subtle">
            <Text color="text.secondary">PLUME Earnings</Text>
            <ValueText loading={loading}>
              {isAuthenticated ? <FormatNumber value={wPlumeEarnings} maximumFractionDigits={2} /> : '--'}
            </ValueText>
          </HStack>
          <HStack justifyContent="space-between" py={3} w="full">
            <Text color="text.secondary">TVL</Text>
            <ValueText loading={loading}>
              {tvlData?.tvl_in_usd ? (
                <FormatNumber value={tvlData?.tvl_in_usd} style="currency" currency="USD" />
              ) : (
                '--'
              )}
            </ValueText>
          </HStack>
          <RoundedButton
            w="full"
            mt={4}
            variant="outline"
            borderRadius="full"
            _groupHover={{
              bgColor: 'bg.muted',
            }}
          >
            Deposit <HiMiniArrowRight />
          </RoundedButton>
        </VStack>
      </Card.Body>
      <Card.Body hideBelow="lg">
        <HStack w="full" alignItems="start">
          <Box flex={1}>
            <CategoryText>Vault Position</CategoryText>
            <ValueText loading={loading}>
              {isAuthenticated ? (
                <>
                  <FormatNumber value={yourDeposits} /> {vaultKey}
                </>
              ) : (
                '--'
              )}
            </ValueText>
            {inputTokenTotals?.tokenAmount ? (
              <ValueText loading={false} fontSize="sm" color="fg.muted" fontFamily="body">
                <>
                  <FormatNumber value={inputTokenTotals?.tokenAmount} /> held in Royco
                </>
              </ValueText>
            ) : null}
          </Box>
          <Box flex={1}>
            <Flex alignItems="center">
              <CategoryText>Total Balance</CategoryText>
              <TooltipIcon ml={1} content={TOTAL_BALANCE_TOOLTIP_TEXT} />
            </Flex>
            <ValueText loading={loading}>
              {isAuthenticated ? <FormatNumber value={totalBalance} style="currency" currency="USD" /> : '--'}
            </ValueText>
          </Box>
          <Box flex={1}>
            <CategoryText>PLUME Earnings</CategoryText>
            <ValueText loading={loading}>
              {isAuthenticated ? <FormatNumber value={wPlumeEarnings} maximumFractionDigits={2} /> : '--'}
            </ValueText>
          </Box>
          <Box flex={1}>
            <CategoryText>TVL</CategoryText>
            <ValueText loading={loading}>
              {tvlData?.tvl_in_usd ? (
                <FormatNumber value={tvlData?.tvl_in_usd} style="currency" currency="USD" />
              ) : (
                '--'
              )}
            </ValueText>
          </Box>
          <Box flex={1}>
            <CategoryText>Rewards</CategoryText>
            <HoverCard.Root>
              <HoverCard.Trigger asChild disabled={isLoadingApyData}>
                <HStack>
                  <Image src="/assets/optimizer/sparkles-dark.svg" />
                  <ValueText loading={isLoadingApyData}>
                    {(apyData?.boostIncentivesList?.length ?? 0) + 1} Rewards
                  </ValueText>
                </HStack>
              </HoverCard.Trigger>
              <Portal>
                <HoverCard.Positioner>
                  <HoverCard.Content w={250}>
                    <HoverCard.Arrow>
                      <HoverCard.ArrowTip />
                    </HoverCard.Arrow>
                    <CategoryText fontSize="sm">Optimizer Vault Yield</CategoryText>
                    <IncentiveRow
                      apy={apyData?.underlyingRate ?? 0}
                      name="Base APY"
                      loading={isLoadingApyData}
                      icon={
                        <Icon color="fg.muted" boxSize={6}>
                          <PiChartBarFill />
                        </Icon>
                      }
                      tooltip={vaultMetadata.apyOverride ? ESTIMATED_APY_TOOLTIP_TEXT : undefined}
                    />
                    <CategoryText fontSize="sm">Royco Boosted Yield</CategoryText>
                    {apyData?.boostIncentivesList?.map((token) => (
                      <IncentiveRow
                        key={token.tokenSymbol}
                        apyColor="#108258"
                        apy={token.apy}
                        name={token.tokenSymbol}
                        icon={<Image src={getIconPathFromTokenSymbol(token.tokenSymbol)} boxSize={6} />}
                        loading={isLoadingApyData}
                      />
                    ))}
                  </HoverCard.Content>
                </HoverCard.Positioner>
              </Portal>
            </HoverCard.Root>
          </Box>
        </HStack>
      </Card.Body>
      <Card.Body borderTopWidth="thin" hideBelow="lg">
        <HStack justifyContent="space-between">
          <Image src={logosPath} h={75} />
          <RoundedButton
            h={16}
            w={224}
            variant="outline"
            borderRadius="full"
            _groupHover={{
              bgColor: 'bg.muted',
            }}
          >
            Deposit <HiMiniArrowRight />
          </RoundedButton>
        </HStack>
      </Card.Body>
      {yourDeposits ? (
        <Card.Body bgColor="#FEF5E8" p={4}>
          <HStack w="full" justifyContent="space-between" fontSize="sm" fontWeight="500">
            <Text>Maximize your APY</Text>
            <Text color="fg.muted">
              <FormatNumber value={yourDeposits} /> {vaultKey} available for Royco Deposit
            </Text>
          </HStack>
        </Card.Body>
      ) : null}
    </Card.Root>
  );
};

const IncentiveRow = ({
  apy,
  name,
  icon,
  apyColor = 'fg',
  loading,
  tooltip,
}: {
  apy: number;
  name: string;
  icon: ReactElement;
  apyColor?: string;
  loading: boolean;
  tooltip?: string;
}) => (
  <HStack my={2} w="full">
    {icon}
    <Text fontSize="md" fontWeight="500">
      {name}
    </Text>
    <Spacer flex={1} />
    <TooltipText
      content={tooltip}
      text={
        <ValueText color={apyColor} loading={loading}>
          <FormatNumber value={apy} style="percent" />
        </ValueText>
      }
    />
  </HStack>
);

const CategoryText = (props: TextProps) => <Text fontSize="md" color="fg.muted" fontWeight="500" {...props} />;

const ValueText = ({ loading, ...props }: TextWithLoadingStateProps) => (
  <TextWithLoadingState fontSize="lg" color="fg" fontWeight="500" fontFamily="heading" loading={loading} {...props} />
);

export const StrategyCardSkeleton = () => {
  return (
    <Card.Root borderRadius="3xl" overflow="hidden" mb={6} w="full">
      <Card.Body>
        <HStack gap={4} justifyContent="space-between">
          <Skeleton boxSize={12} borderRadius="full" />
          <VStack align="start" gap={1} flex={1}>
            <Skeleton height="24px" width="150px" />
            <Skeleton height="18px" width="100px" />
          </VStack>
          <Spacer />
          <HStack>
            <Skeleton boxSize={6} />
            <Skeleton boxSize={6} />
            <Skeleton boxSize={6} />
          </HStack>
          <Skeleton height="32px" width="100px" borderRadius="full" />
        </HStack>
      </Card.Body>

      <Card.Body>
        <HStack w="full" alignItems="start" gap={6}>
          {[...Array(5)].map((_, i) => (
            <VStack align="start" gap={2} flex={1} key={i}>
              <Skeleton height="16px" width="100px" />
              <Skeleton height="24px" width="100%" />
              <Skeleton height="16px" width="80%" />
            </VStack>
          ))}
        </HStack>
      </Card.Body>

      <Card.Body borderTopWidth="thin">
        <HStack justifyContent="space-between">
          <Skeleton h="75px" w="150px" />
          <Skeleton h="64px" w="224px" borderRadius="full" />
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};
